import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Upload, Download, CheckCircle, Clock, AlertCircle, TrendingUp, LogOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const ContadorPanel = () => {
  const [facturas, setFacturas] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({ pdf: null, xml: null });
  const [filterEstado, setFilterEstado] = useState('all');
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkContador();
  }, []);

  const checkContador = async () => {
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) {
      toast({
        title: 'Acceso denegado',
        description: 'Debes iniciar sesión',
        variant: 'destructive',
      });
      navigate('/login');
      return;
    }
    loadData();
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [facturasResponse, estadisticasResponse] = await Promise.all([
        api.get('/facturacion/todas'),
        api.get('/facturacion/estadisticas')
      ]);
      setFacturas(facturasResponse.data);
      setEstadisticas(estadisticasResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (facturaId, type, file) => {
    setSelectedFiles(prev => ({
      ...prev,
      [type]: { facturaId, file }
    }));
  };

  const handleUploadFactura = async (facturaId) => {
    if (!selectedFiles.pdf || !selectedFiles.xml) {
      toast({
        title: 'Error',
        description: 'Debes seleccionar ambos archivos (PDF y XML)',
        variant: 'destructive',
      });
      return;
    }

    if (selectedFiles.pdf.facturaId !== facturaId || selectedFiles.xml.facturaId !== facturaId) {
      toast({
        title: 'Error',
        description: 'Los archivos no coinciden con la factura',
        variant: 'destructive',
      });
      return;
    }

    setUploadingId(facturaId);
    const formData = new FormData();
    formData.append('pdf', selectedFiles.pdf.file);
    formData.append('xml', selectedFiles.xml.file);

    try {
      await api.post(`/facturacion/${facturaId}/upload-archivos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast({
        title: '✅ Factura completada',
        description: 'Los archivos han sido subidos exitosamente',
      });

      setSelectedFiles({ pdf: null, xml: null });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudieron subir los archivos',
        variant: 'destructive',
      });
    } finally {
      setUploadingId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    navigate('/login');
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      'Pendiente': { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Clock },
      'Procesando': { color: 'bg-blue-100 text-blue-700 border-blue-300', icon: TrendingUp },
      'Completada': { color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle },
      'Error': { color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle }
    };
    return badges[estado] || badges['Pendiente'];
  };

  const facturasFiltradas = filterEstado === 'all' 
    ? facturas 
    : facturas.filter(f => f.estado === filterEstado);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e40af] mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-6 shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-1">Panel del Contador</h1>
              <p className="text-blue-100">Gestión de Facturas - MoraBioVet</p>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="bg-white/10 hover:bg-white/20 text-white border-white/30"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Estadísticas */}
        {estadisticas && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-white shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total</p>
                    <p className="text-3xl font-bold text-gray-800">{estadisticas.total}</p>
                  </div>
                  <FileText className="h-10 w-10 text-gray-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-yellow-50 shadow-md hover:shadow-lg transition-shadow border-yellow-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-700 mb-1">Pendientes</p>
                    <p className="text-3xl font-bold text-yellow-800">{estadisticas.pendientes}</p>
                  </div>
                  <Clock className="h-10 w-10 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-blue-50 shadow-md hover:shadow-lg transition-shadow border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700 mb-1">Procesando</p>
                    <p className="text-3xl font-bold text-blue-800">{estadisticas.procesando}</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-green-50 shadow-md hover:shadow-lg transition-shadow border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700 mb-1">Completadas</p>
                    <p className="text-3xl font-bold text-green-800">{estadisticas.completadas}</p>
                  </div>
                  <CheckCircle className="h-10 w-10 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-red-50 shadow-md hover:shadow-lg transition-shadow border-red-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-700 mb-1">Errores</p>
                    <p className="text-3xl font-bold text-red-800">{estadisticas.errores}</p>
                  </div>
                  <AlertCircle className="h-10 w-10 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filtros */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex gap-3 flex-wrap">
              <Button
                variant={filterEstado === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterEstado('all')}
                className={filterEstado === 'all' ? 'bg-[#1e40af]' : ''}
              >
                Todas ({facturas.length})
              </Button>
              <Button
                variant={filterEstado === 'Pendiente' ? 'default' : 'outline'}
                onClick={() => setFilterEstado('Pendiente')}
                className={filterEstado === 'Pendiente' ? 'bg-yellow-600' : ''}
              >
                Pendientes ({estadisticas?.pendientes || 0})
              </Button>
              <Button
                variant={filterEstado === 'Procesando' ? 'default' : 'outline'}
                onClick={() => setFilterEstado('Procesando')}
                className={filterEstado === 'Procesando' ? 'bg-blue-600' : ''}
              >
                Procesando ({estadisticas?.procesando || 0})
              </Button>
              <Button
                variant={filterEstado === 'Completada' ? 'default' : 'outline'}
                onClick={() => setFilterEstado('Completada')}
                className={filterEstado === 'Completada' ? 'bg-green-600' : ''}
              >
                Completadas ({estadisticas?.completadas || 0})
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Lista de Facturas */}
        <div className="space-y-4">
          {facturasFiltradas.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-gray-500">
                <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>No hay facturas con el filtro seleccionado</p>
              </CardContent>
            </Card>
          ) : (
            facturasFiltradas.map((factura) => {
              const estadoBadge = getEstadoBadge(factura.estado);
              const IconoEstado = estadoBadge.icon;

              return (
                <Card key={factura.id} className="shadow-md hover:shadow-lg transition-shadow border-2">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Información de la factura */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-bold text-[#1e3a8a] mb-1">
                              Factura #{factura.id.substring(0, 8)}
                            </h3>
                            <p className="text-sm text-gray-600">
                              Solicitada el {new Date(factura.fecha_solicitud).toLocaleDateString('es-MX')}
                            </p>
                          </div>
                          <div className={`px-4 py-2 rounded-full border-2 flex items-center gap-2 ${estadoBadge.color}`}>
                            <IconoEstado className="h-4 w-4" />
                            <span className="font-semibold text-sm">{factura.estado}</span>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-500">Cliente:</span>
                              <span className="ml-2 font-medium">{factura.user_nombre}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">RFC:</span>
                              <span className="ml-2 font-medium">{factura.rfc}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Razón Social:</span>
                              <span className="ml-2 font-medium">{factura.razon_social}</span>
                            </div>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-500">Uso CFDI:</span>
                              <span className="ml-2 font-medium">{factura.uso_cfdi}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Monto:</span>
                              <span className="ml-2 font-bold text-green-600">${factura.monto_total} MXN</span>
                            </div>
                            {factura.procesado_por_nombre && (
                              <div>
                                <span className="text-gray-500">Procesado por:</span>
                                <span className="ml-2 font-medium">{factura.procesado_por_nombre}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Acciones */}
                      <div className="w-full md:w-80 border-l pl-6">
                        {factura.estado === 'Completada' ? (
                          <div className="space-y-3">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                              <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                              <p className="text-sm font-semibold text-green-800">Factura Completada</p>
                            </div>
                            {factura.factura_pdf_url && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={() => window.open(factura.factura_pdf_url, '_blank')}
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Descargar PDF
                              </Button>
                            )}
                            {factura.factura_xml_url && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={() => window.open(factura.factura_xml_url, '_blank')}
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Descargar XML
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <h4 className="font-semibold text-gray-700 mb-3">Subir Factura Timbrada</h4>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Archivo PDF
                              </label>
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => handleFileSelect(factura.id, 'pdf', e.target.files[0])}
                                className="w-full text-sm"
                              />
                              {selectedFiles.pdf?.facturaId === factura.id && (
                                <p className="text-xs text-green-600 mt-1">✓ {selectedFiles.pdf.file.name}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Archivo XML
                              </label>
                              <input
                                type="file"
                                accept=".xml"
                                onChange={(e) => handleFileSelect(factura.id, 'xml', e.target.files[0])}
                                className="w-full text-sm"
                              />
                              {selectedFiles.xml?.facturaId === factura.id && (
                                <p className="text-xs text-green-600 mt-1">✓ {selectedFiles.xml.file.name}</p>
                              )}
                            </div>

                            <Button
                              onClick={() => handleUploadFactura(factura.id)}
                              disabled={uploadingId === factura.id || 
                                       selectedFiles.pdf?.facturaId !== factura.id || 
                                       selectedFiles.xml?.facturaId !== factura.id}
                              className="w-full bg-[#1e40af] hover:bg-[#1e3a8a]"
                            >
                              {uploadingId === factura.id ? (
                                <>Subiendo...</>
                              ) : (
                                <>
                                  <Upload className="h-4 w-4 mr-2" />
                                  Marcar como Elaborada
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ContadorPanel;
